// Cookie/analytics consent state. GA4 events are gated behind this.
//
// Storage: localStorage key `refertle_consent_v1` = { analytics: boolean, ts: number }
// The same key is read by the bootstrap script in index.html so consent
// persists across reloads and is replayed into Google Consent Mode v2.

export type ConsentState = {
  analytics: boolean;
  decided: boolean;
};

const STORAGE_KEY = "refertle_consent_v1";
const EVENT_NAME = "refertle:consent-changed";

const readRaw = (): { analytics?: boolean; ts?: number } | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const getConsent = (): ConsentState => {
  const raw = readRaw();
  if (!raw) return { analytics: false, decided: false };
  return { analytics: raw.analytics === true, decided: true };
};

export const hasAnalyticsConsent = (): boolean => getConsent().analytics;

const updateGtagConsent = (analytics: boolean) => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("consent", "update", {
    analytics_storage: analytics ? "granted" : "denied",
    functionality_storage: analytics ? "granted" : "denied",
  });
};

export const setConsent = (analytics: boolean) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ analytics, ts: Date.now() }),
    );
  } catch {
    // ignore storage failures
  }
  updateGtagConsent(analytics);
  window.dispatchEvent(
    new CustomEvent(EVENT_NAME, { detail: { analytics, decided: true } }),
  );
};

export const subscribeConsent = (cb: (state: ConsentState) => void) => {
  if (typeof window === "undefined") return () => {};
  const handler = () => cb(getConsent());
  window.addEventListener(EVENT_NAME, handler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(EVENT_NAME, handler);
    window.removeEventListener("storage", handler);
  };
};
